import { Typography, Grid, Checkbox } from '@mui/material';
import AccordionGridWrapper from 'components/common/AccordionGridWrapper';
import StickyHeaderWrapper from 'components/common/StickyHeaderWrapper';
import ConfigurationForm from 'components/productConfiguration/ConfigurationForm';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { AppDispatch, RootState } from 'store';
import { jbColors } from 'theme/jbColors';
import { getModificationData, sendModificationData } from 'thunks/accountServiceAgreementsThunk';
import { uiSchema } from 'utils/json-form/formDataUtils';
import BackToWorkflowOverviewIcon from 'assets/icons/return-workflow-overview-icon-blue.svg?react';
import RightSideBottomBar from 'components/common/RightSideBottomBar';
import { LoadingSpinner } from 'components/loadingSpinner/LoadingSpinner';
import { ServiceAgreementModificationRequest } from 'pco-product-service-internal-consumer';

type ProductModificationProps = {
  brUid?: string | null;
  partnerUid?: string | null;
};

const ProductModification = ({ brUid, partnerUid }: ProductModificationProps) => {
  const selectedServiceAgreement = useSelector((state: RootState) => state.serviceAgreements.selectedServiceAgreement);
  const formData = useSelector((state: RootState) => state.productConfiguration.formData);
  const modificationData = useSelector((state: RootState) => state.serviceAgreements.modificationData);
  const [, setIsFormValid] = useState<boolean>(false);
  const [forceRepapering, setForceRepapering] = useState<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const agreementDetailsUrl = brUid
    ? `/accounts/${brUid}/serviceAgreement/${selectedServiceAgreement?.serviceAgreementId}/details`
    : `/partner/${partnerUid}/serviceAgreement/${selectedServiceAgreement?.serviceAgreementId}/details`;
  const { loader } = useSelector((state: RootState) => state.appConfig);

  const send = () => {
    if (!modificationData) return;
    const tempModificationData: ServiceAgreementModificationRequest = {
      productAttributeId: modificationData.productAttributeId ?? '',
      updatedForm: JSON.stringify(formData ?? JSON.parse(modificationData.formValues ?? '{}')),
      forceRepapering: forceRepapering,
    };

    if (selectedServiceAgreement?.serviceAgreementId)
      dispatch(
        sendModificationData(selectedServiceAgreement?.serviceAgreementId, tempModificationData, () =>
          navigate(agreementDetailsUrl),
        ),
      );
  };

  const handleBack = () => {
    navigate(agreementDetailsUrl);
  };

  useEffect(() => {
    if (selectedServiceAgreement?.serviceAgreementId) {
      dispatch(getModificationData(selectedServiceAgreement?.serviceAgreementId ?? ''));
    }
  }, [selectedServiceAgreement, dispatch]);

  return (
    <>
      <span data-testid="product-modification-view"></span>
      {loader.loading ? (
        <LoadingSpinner message={loader.message} />
      ) : (
        <StickyHeaderWrapper
          topComponents={
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                ml: 0.5,
                mb: 2.5,
                mt: 3.5,
              }}
            >
              <Grid
                data-testid="product-configuration"
                item
                onClick={handleBack}
                xs={3}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  p: 1,
                  mt: 1,
                  ml: -0.6,
                  width: 'fit-content',
                  border: `1px solid ${jbColors.core.stone[30]}`,
                  borderBottom: 'none',
                  pb: 1,
                  backgroundColor: 'white',
                  ':hover': {
                    backgroundColor: jbColors.core.stone[40],
                    color: jbColors.core.reflexBlue[100],
                    border: `1px solid ${jbColors.core.stone[30]}`,
                  },
                }}
              >
                <Grid item xs={1}>
                  <BackToWorkflowOverviewIcon
                    style={{
                      display: 'flex',
                      color: jbColors.core.reflexBlue[100],
                      height: '1em',
                      width: '0.6em',
                      marginRight: '0.5em',
                    }}
                  />
                </Grid>
                <Grid item xs={11}>
                  {' '}
                  <Typography variant={'body2'} sx={{ color: jbColors.core.reflexBlue[100] }}>
                    Back to Details
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          }
          centerComponents={
            <AccordionGridWrapper title={'Product configuration'}>
              {modificationData &&
                modificationData.jsonSchema &&
                modificationData.jsonSchema !== 'No schema found.' && (
                  <>
                    <ConfigurationForm
                      formValues={formData ?? JSON.parse(modificationData.formValues ?? '{}')}
                      propsUiSchema={{ ...uiSchema, ...JSON.parse(modificationData.uiSchema ?? '{}') }}
                      setIsFormValid={setIsFormValid}
                      schema={JSON.parse(modificationData.jsonSchema)}
                    />
                    {modificationData.forceRepaperingEnabled && (
                      <Grid container>
                        <Grid
                          item
                          xs={12}
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                          }}
                        >
                          <Checkbox value={forceRepapering} onChange={() => setForceRepapering(!forceRepapering)} />{' '}
                          Check this checkbox to create a task that will enforce provision of a new contract /
                          amendment.
                        </Grid>
                      </Grid>
                    )}
                  </>
                )}
            </AccordionGridWrapper>
          }
          side={'right'}
          hasStickyBottomBar={true}
        />
      )}
      {!loader.loading && (
        <RightSideBottomBar>
          <Grid container>
            <Grid item xs={12} onClick={send} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Typography
                variant={'body2'}
                sx={{
                  backgroundColor: jbColors.core.reflexBlue[100],
                  color: 'white',
                  p: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'white',
                    color: jbColors.core.reflexBlue[100],
                  },
                  '&:disabled': {
                    border: `1px solid ${jbColors.support.solidGrayIcon[10]}`,
                    backgroundColor: jbColors.core.stone[10],
                    color: jbColors.support.solidGrayIcon[10],
                  },
                }}
              >
                Submit edit
              </Typography>
            </Grid>
          </Grid>
        </RightSideBottomBar>
      )}
    </>
  );
};

export default ProductModification;
